import * as Cookie from 'es-cookie';
import { DateTime } from 'luxon';
import {
  AD_CONSENT_AT_COOKIE,
  AD_CONSENT_COOKIE,
  AdConsentAnswer,
  DatedAdConsent,
  GPC_SINCE_COOKIE,
  adConsentUnderGpc,
  nextGpcSince,
  readDeviceAdConsent,
} from '@cf-shared/ad-consent';

/**
 * This browser's Advertising answer (#5587), kept in two cookies: the answer
 * (`'1'` / `'0'`, the value the cookie dialog has always written) and the time it
 * was given. The rules for reading them live in `shared/src/ad-consent.ts`.
 */

// the options every consent cookie is written with
const COOKIE_OPTIONS: Cookie.CookieAttributes = { sameSite: 'strict', expires: 3000 };

/** Write one consent category's cookie, `_cf_consent_<id>`. The one place the
 *  cookie options live, so no consent cookie is written with different ones. */
export function writeConsentCookie(id: string, value: string) {
  Cookie.set('_cf_consent_' + id, value, COOKIE_OPTIONS);
}

/** The answer this browser holds, or undefined when there is none worth trusting. */
export function readAdConsentCookies(): DatedAdConsent | undefined {
  return readDeviceAdConsent(Cookie.get(AD_CONSENT_COOKIE), Cookie.get(AD_CONSENT_AT_COOKIE));
}

/**
 * Store an answer and the time it was given. `atIso` is the account record's
 * time when the answer is copied from the account, and now when the person just
 * answered.
 *
 * Passing NO time removes the time cookie rather than inventing one, which is
 * the honest record for an account record that carries none (an old or damaged
 * document). It is not a cosmetic difference: `readDeviceAdConsent` already
 * treats an undated "1" as no answer and an undated "0" as a real no, and since
 * #5663 a fabricated time would also read as "given after the Global Privacy
 * Control signal" and silently override it.
 */
export function writeAdConsentCookies(answer: AdConsentAnswer, atIso?: string) {
  // AD_CONSENT_COOKIE is `_cf_consent_ad`, so this is the 'ad' category's cookie
  writeConsentCookie('ad', answer === AdConsentAnswer.Yes ? '1' : '0');
  if (atIso) Cookie.set(AD_CONSENT_AT_COOKIE, atIso, COOKIE_OPTIONS);
  else Cookie.remove(AD_CONSENT_AT_COOKIE, COOKIE_OPTIONS);
}

/**
 * Whether THIS browser says the Global Privacy Control signal is on (#5663).
 *
 * The property is not in the DOM typings, because most browsers do not have it,
 * and anything a page can set can also be set to nonsense — so only the literal
 * boolean `true` counts. A browser that sets only the HTTP header answers false
 * here, and is caught on the server instead.
 */
function browserSaysGpc(): boolean {
  return (navigator as { globalPrivacyControl?: unknown }).globalPrivacyControl === true;
}

/**
 * Record that the signal was, or was not, seen right now, and answer with the
 * stamp to judge answers against.
 *
 * `alsoSeenByServer` is for the one thing the browser cannot see: a request
 * header our own server read. The Paddle checkout config carries that answer
 * back, and passing it here is what makes a header-only browser count.
 *
 * The rule about when the stamp moves — and the reasons it is never cleared —
 * lives in `nextGpcSince`, not here.
 */
export function noteGpcSeen(alsoSeenByServer = false): string | undefined {
  const stored = Cookie.get(GPC_SINCE_COOKIE);
  const since = nextGpcSince(browserSaysGpc() || alsoSeenByServer, stored, DateTime.utc().toISO());
  if (since && since !== stored) Cookie.set(GPC_SINCE_COOKIE, since, COOKIE_OPTIONS);
  return since;
}

/**
 * The Advertising answer to ACT on: this browser's answer with the Global
 * Privacy Control signal applied (#5663).
 *
 * Every reader that needs only THIS BROWSER's answer goes through it — the
 * checkout blob, the cookie dialog's toggle and the subscribe page's "has the
 * answer changed" check. They have to agree: that last check compares the answer
 * a checkout was built with against the answer now, so one reader applying the
 * signal and another not would look like a change on every pass and reopen a
 * checkout the buyer is typing into.
 *
 * The Terms screen is not a reader at all: its Advertising box always starts
 * unticked and asks afresh.
 *
 * It is `resolve`, not `read`, because it WRITES: noting the signal is what keeps
 * the stamp current, and a reader that quietly skipped that would be the silent
 * version of not honouring the signal at all.
 */
export function resolveEffectiveAdConsent(alsoSeenByServer = false): AdConsentAnswer | undefined {
  return adConsentUnderGpc(readAdConsentCookies(), noteGpcSeen(alsoSeenByServer));
}
